// eslint:disable max-func-body-length
import { useRef } from 'react';
import { ValidationLevel, ValidationProps } from 'ui';
import { routeConstants } from 'app/routes/route-constants';
import { isDefinedIncludingZero } from 'app/infrastructure/data/number';
import { Configuration } from 'app/components/metro-line/types';
import { FavouriteAdminTranslation } from '../../constants';
import { FavouriteItemValidation } from '../types-store';
import { FavouriteSectionType } from './types';

const isValid = (validationProps: ValidationProps[], level: ValidationLevel) =>
  !validationProps.some(prop => (prop?.messages?.length ?? 0) > 0 && prop?.level === level);

export const getFavouriteRoute = (
  favouriteId: number,
  section?: FavouriteSectionType
) => isDefinedIncludingZero(favouriteId) && routeConstants.favouriteAdminItemFor(favouriteId, section);

export const favouriteConfiguration = (
  companyReportId: number,
  showSchedule: boolean,
  showRecipients: boolean,
  validationErrors: FavouriteItemValidation
): Configuration<FavouriteSectionType>[] => {

  const detailsSectionRef = useRef<HTMLDivElement>();
  const sharedWithSectionRef = useRef<HTMLDivElement>();
  const dashboardsSectionRef = useRef<HTMLDivElement>();
  const companyReportsSectionRef = useRef<HTMLDivElement>();
  const scheduledReportSectionRef = useRef<HTMLDivElement>();
  const recipientsSectionRef = useRef<HTMLDivElement>();
  const translationsSectionRef = useRef<HTMLDivElement>();

  const getSectionRoute = (section: FavouriteSectionType) => getFavouriteRoute(companyReportId, section);

  const renderLanguageOnTop = !isDefinedIncludingZero(companyReportId);
  const languageSection = {
    section: FavouriteSectionType.Translations,
    visible: true,
    url: getSectionRoute(FavouriteSectionType.Translations),
    iconUrl: '/i/case-sections/actions.svg',
    label: (translation: FavouriteAdminTranslation) => translation.descriptions_and_translations,
    ref: translationsSectionRef,
    isValid: isValid([validationErrors.language], ValidationLevel.error)
  };

  return [
    ...(renderLanguageOnTop ? [languageSection] : []),
    {
      section: FavouriteSectionType.Details,
      visible: true,
      iconUrl: '/i/case-sections/default.svg',
      url: getSectionRoute(FavouriteSectionType.Details),
      label: (translation: FavouriteAdminTranslation) => translation.details,
      ref: detailsSectionRef,
      isValid: isValid([
        validationErrors.parent, validationErrors.disabled, validationErrors.favouriteType,
        validationErrors.owner
      ], ValidationLevel.error)
    },
    {
      section: FavouriteSectionType.SharedWith,
      visible: true,
      iconUrl: '/i/case-sections/where-and-what.svg',
      url: getSectionRoute(FavouriteSectionType.SharedWith),
      label: (translation: FavouriteAdminTranslation) => translation.shared_with,
      ref: sharedWithSectionRef,
      isValid: isValid(
        [
          validationErrors.sharedWithUserGroups, validationErrors.sharedWithUsers
        ], ValidationLevel.error)
    },
    {
      section: FavouriteSectionType.Dashboards,
      visible: true,
      iconUrl: '/i/case-sections/where-and-what.svg',
      url: getSectionRoute(FavouriteSectionType.Dashboards),
      label: (translation: FavouriteAdminTranslation) => translation.dashboards_title,
      ref: dashboardsSectionRef,
      isValid: true
    },
    {
      section: FavouriteSectionType.CompanyReports,
      visible: true,
      iconUrl: '/i/case-sections/where-and-what.svg',
      url: getSectionRoute(FavouriteSectionType.CompanyReports),
      label: (translation: FavouriteAdminTranslation) => translation.company_reports_title,
      ref: companyReportsSectionRef,
      isValid: true
    },
    {
      section: FavouriteSectionType.Schedule,
      visible: showSchedule,
      iconUrl: '/i/case-sections/where-and-what.svg',
      url: getSectionRoute(FavouriteSectionType.Schedule),
      label: (translation: FavouriteAdminTranslation) => translation.schedule,
      ref: scheduledReportSectionRef,
      isValid: true
    },
    {
      section: FavouriteSectionType.Recipients,
      visible: showRecipients,
      iconUrl: '/i/case-sections/where-and-what.svg',
      url: getSectionRoute(FavouriteSectionType.Recipients),
      label: (translation: FavouriteAdminTranslation) => translation.recipients,
      ref: recipientsSectionRef,
      isValid: isValid([validationErrors.emailSubscribers], ValidationLevel.error)
    },
    ...(!renderLanguageOnTop ? [languageSection] : [])
  ];
};
